import type { TaskStatus } from './task'
import {
  ATLAS_PRODUCT_BOARD_PROJECT_NAME,
  TRIAGE_BOARD_FIELD_NAMES,
  TRIAGE_SECTION_NAMES,
} from './taskTriage'

export const FEEDBACK_CATEGORIES = ['bug', 'feature_request', 'general'] as const
export const FEEDBACK_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number]

/**
 * Structured analysis Arc gathers about a feedback submission — stored on
 * `feedback_submission.arc_analysis` to help engineers act on it.
 */
export interface IFeedbackAnalysis {
  /** One-line restatement of what the user is asking for. */
  summary: string
  /** What Arc determined from the screenshot + code search. */
  findings: string
  /** Repo file paths Arc identified as relevant. */
  relevantFiles: string[]
  /** How an engineer could implement the change. */
  implementationNotes: string
  /** Why/how this is relevant, plus Arc's confidence in the analysis. */
  relevancy: string
}

/** A single follow-up question/answer pair captured on a submission. */
export interface IFeedbackTranscriptItem {
  question: string
  answer: string
}

/** Where every feedback submission is filed: the Atlas Product Board's Inbound. */
export const FEEDBACK_BOARD_NAME = ATLAS_PRODUCT_BOARD_PROJECT_NAME
export const FEEDBACK_BOARD_SECTION_NAME = TRIAGE_SECTION_NAMES.inbound
export const FEEDBACK_BOARD_FIELD_NAMES = {
  type: TRIAGE_BOARD_FIELD_NAMES.type,
  priority: TRIAGE_BOARD_FIELD_NAMES.priority,
  stakeholders: TRIAGE_BOARD_FIELD_NAMES.stakeholders,
} as const

/** Triage Type for a submission; general feedback is left blank for a person to triage. */
export const resolveFeedbackTypeName = (category: string, priority: string): string | null => {
  if (category === 'bug') {
    return ['high', 'critical'].includes(priority.toLowerCase()) ? 'Major Bug' : 'Minor Bug'
  }
  if (category === 'feature_request') {
    return 'Enhancement'
  }
  return null
}

/** The board's Priority option for a submission priority; Critical has no option of its own. */
export const resolveFeedbackPriorityName = (priority: string): string | null => {
  const normalized = priority.toLowerCase()
  if (normalized === 'critical' || normalized === 'high') return 'High'
  if (normalized === 'medium') return 'Medium'
  if (normalized === 'low') return 'Low'
  return null
}

/**
 * Live-derived status for a feedback submission shown on the Feedback page.
 * There is NO stored lifecycle field — status is computed from the linked
 * ticket (Asana task or ACT task) each time the list is read.
 */
export const FEEDBACK_STATUS = {
  COMPLETED: 'Completed',
  OPEN: 'Open',
  NO_TICKET: 'Ticket not available',
} as const

export type FeedbackStatus = (typeof FEEDBACK_STATUS)[keyof typeof FEEDBACK_STATUS]

/**
 * Single source of truth for the status mapping. `completed === true` →
 * "Completed", a linked ticket that isn't complete → "Open", and no linked
 * ticket → "Ticket not available". Any failure resolving the ticket defaults
 * to "Open" (see the feedback List service).
 */
export const resolveFeedbackStatus = (
  ticketId: string | null | undefined,
  completed?: boolean | null,
): FeedbackStatus => {
  if (!ticketId) return FEEDBACK_STATUS.NO_TICKET
  return completed ? FEEDBACK_STATUS.COMPLETED : FEEDBACK_STATUS.OPEN
}

/**
 * Metadata read live from the linked ticket. Null when the submission has no
 * linked ticket or it couldn't be resolved.
 */
export interface IFeedbackTicketMeta {
  /** The ticket's Type value (e.g. "Minor Bug", "Enhancement"). */
  type: string | null
  /** The ticket's assignee, when one is set. */
  assignee: { name: string; photoUrl: string | null } | null
  /** ISO date the ticket was completed, when completed. */
  completedAt: string | null
}

/** Someone who has submitted feedback, as the Submitter filter lists them. */
export interface IFeedbackSubmitter {
  id: string
  name: string
}

/** A feedback submission as returned by the feedback List API. */
export interface IFeedbackSubmission {
  id: string
  userId: string
  pageUrl: string | null
  title: string
  description: string
  category: string
  priority: string
  screenshotUrl: string | null
  recordingUrl: string | null
  consoleLogs: string[] | null
  arcTranscript: IFeedbackTranscriptItem[] | null
  arcAnalysis: IFeedbackAnalysis | null
  asanaTaskId: string | null
  /** The ACT task filed for this submission, when it went to ACT. */
  actTaskId: string | null
  attachmentUrls: string[]
  createdAt: string
  user?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  /** The ACT task's own status when it has one, else derived — see resolveFeedbackStatus. */
  status: FeedbackStatus | TaskStatus
  /** Permalink to the linked Asana task, when one exists. */
  asanaPermalinkUrl: string | null
  /** Live-derived ticket metadata (Type / assignee / completed date). */
  ticketMeta: IFeedbackTicketMeta | null
}

export interface IFeedbackListData {
  data: IFeedbackSubmission[]
  total: number
}

/** A feedback file, with a short-lived link to open it. */
export interface IFeedbackFile {
  name: string
  kind: 'screenshot' | 'recording' | 'attachment'
  mimeType: string | null
  url: string
}

/** The feedback behind an ACT task, as the task panel's Feedback section shows it. */
export interface ITaskFeedback {
  id: string
  submitterName: string
  submitterEmail: string | null
  createdAt: string
  pageUrl: string | null
  /** The submitter's own title; the task's may have been groomed since. */
  title: string
  description: string
  hasScreenshot: boolean
  consoleLogs: string[] | null
  arcTranscript: IFeedbackTranscriptItem[] | null
}
